import {Component, OnInit, signal} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {TranslatePipe} from '@ngx-translate/core';
import {MatFormField, MatFormFieldModule, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';

import {
  DynamicConfirmationDialogComponent
} from '../../components/dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {ActivatedRoute, Router} from '@angular/router';
import {DialogService} from '../../services/dialog.service';
import {UserSelectionComponent} from '../../components/user-selection/user-selection.component';
import {UserHal} from '../../models/HAL/user-hal.model';
import {CaseRequestInformationHal} from '../../models/HAL/caseRequestInformation-hal.model';
import {MatOption, MatSelect} from '@angular/material/select';
import {HttpClient, HttpContext} from '@angular/common/http';
import {SnackBarService} from '../../services/snackbar.service';
import {DocumentCollectionHal, DocumentHal} from '../../models/HAL/document-hal.model';
import {MatExpansionModule} from '@angular/material/expansion';
import {RequestInformationActionDto} from '../../models/DTO/RequestInformationActionDto';
import {RequestInformationDto} from '../../models/DTO/RequestInformationDto';
import {InformationTypeEnum} from '../../enums/information-type.enum';
import {ActionTypeEnum} from '../../enums/action-type.enum';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {catchError, EMPTY, filter, from, switchMap, tap} from 'rxjs';
import {SKIP_EXCEPTION_HANDLING} from '../../tokens/skip-exception-handling.token';
import {Exception} from '../../models/exception';

@Component({
  selector: 'app-request-information',
  imports: [
    MatButton,
    MatSelect,
    TranslatePipe,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatLabel,
    MatIcon,
    MatIconButton,
    MatFormFieldModule,
    UserSelectionComponent,
    MatOption,
    FormsModule,
    MatExpansionModule
  ],
  templateUrl: './request-information.component.html',
  styleUrl: './request-information.component.scss'
})
export class RequestInformationComponent implements OnInit {
  protected caseCustomer = signal<UserHal | undefined>(undefined);
  protected selectedCustomer: UserHal | null = null;
  protected documentsFormControl: FormControl = new FormControl();
  protected commentFormControl: FormControl = new FormControl();
  protected requiredDocumentTitles: string[] = [];
  protected documentsWithFileId: DocumentHal[] = [];
  protected documentsWithoutFileId: DocumentHal[] = [];
  protected selectedDocumentsWithFileId: DocumentHal[] = [];
  protected selectedDocumentsWithoutFileId: DocumentHal[] = [];
  protected caseRequestInformationHal: CaseRequestInformationHal;

  constructor(readonly activatedRoute: ActivatedRoute,
              private readonly snackBarService: SnackBarService,
              private readonly router: Router,
              private readonly httpClient: HttpClient,
              private readonly dialogService: DialogService,
              private readonly loadingSpinnerService: LoadingSpinnerService
  ) {
    this.caseRequestInformationHal = new CaseRequestInformationHal(activatedRoute.snapshot.data['requestInformation']);
  }

  ngOnInit(): void {
    const documentsUrl = new URL(this.caseRequestInformationHal._links.getLink('getDocuments')!.href);
    documentsUrl.searchParams.set('paged', 'false');
    this.httpClient.get<DocumentCollectionHal>(documentsUrl.href).subscribe({
      next: (response: DocumentCollectionHal) => {
        for (let document of response._embedded?.documents || []) {
          if (document.fileMetadata && !!document.fileMetadata.fileId) {
            this.documentsWithFileId.push(document);
          }
          if (!(document.fileMetadata && !!document.fileMetadata.fileId) && document.formIdentifier) {
            this.documentsWithoutFileId.push(document);
          }
        }
      },
      error: () => {
        this.snackBarService.open('requestInformation.snackbar.loadError', 3, 'error');
      }
    });
    const clientUserUrl = this.caseRequestInformationHal._links.getLink('getClientUser')?.href;
    this.httpClient.get<UserHal>(clientUserUrl!).subscribe({
      next: (response: UserHal) => {
        this.caseCustomer.set(response);
        this.selectedCustomer = response;
        this.loadingSpinnerService.hide();
      },
      error: () => {
        this.snackBarService.open('requestInformation.snackbar.loadError', 3, 'error');
        this.loadingSpinnerService.hide();
      }
    });
  }

  protected addRequiredDocumentTitle() {
    if (this.documentsFormControl.value) {
      this.requiredDocumentTitles.push(this.documentsFormControl.value);
      this.documentsFormControl.setValue('');
    }
  }

  protected removeRequiredDocumentTitle(document: string) {
    const index = this.requiredDocumentTitles.indexOf(document);
    if (index >= 0) {
      this.requiredDocumentTitles.splice(index, 1);
    }
  }

  protected backToCase(): void {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'requestInformation.backToCaseModal.title',
        message: 'requestInformation.backToCaseModal.message',
        confirmButtonText: 'requestInformation.backToCaseModal.cancel',
        cancelButtonText: 'requestInformation.backToCaseModal.confirm'
      }
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadingSpinnerService.show();
        this.router.navigate([this.caseRequestInformationHal._links.getLink('case')?.getRoute()]);
      }
    });
  }

  protected customerSelected(userHal: UserHal) {
    this.selectedCustomer = userHal;
  }

  protected customerUnselected() {
    this.selectedCustomer = null;
  }

  protected requestInformation(): void {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'requestInformation.sendRequestModal.title',
        message: 'requestInformation.sendRequestModal.message',
        confirmButtonText: 'requestInformation.sendRequestModal.confirm',
        cancelButtonText: 'requestInformation.sendRequestModal.cancel'
      }
    });

    dialogRef.afterClosed().pipe(
      filter((result: boolean) => result),
      tap(() => {
        this.loadingSpinnerService.show();
      }),
      switchMap(() => this.sendRequestInformation()),
      switchMap(() => from(this.router.navigate([this.caseRequestInformationHal._links.getLink('case')?.getRoute()]))),
      catchError((error: Exception) => {
        this.loadingSpinnerService.hide();
        this.snackBarService.open(this.requestErrorMessage(error), 3, 'error');
        return EMPTY;
      })
    ).subscribe();
  }

  private sendRequestInformation() {
    const requestInformationActionDtos = this.createRequestInformationActionDtos();
    const requestInformationDto = new RequestInformationDto({
      requestInformationActions: requestInformationActionDtos,
      personalMessage: this.commentFormControl.value,
      collaboratorId: this.selectedCustomer?.identification
    });

    return this.httpClient.post<CaseRequestInformationHal>(
      this.caseRequestInformationHal._links.getLink('createRequestInformationActions')!.href,
      requestInformationDto,
      {context: new HttpContext().set(SKIP_EXCEPTION_HANDLING, true)}
    ).pipe(
      tap(() => this.snackBarService.open('requestInformation.snackbar.requestSuccess', 3))
    );
  }

  private requestErrorMessage(error: Exception): string {
    switch (error.clientException?.name) {
      case 'CASE_ACTION_ALREADY_ACTIVE':
        return 'requestInformation.snackbar.actionAlreadyActive';
      case 'CASE_CLIENT_PARTICIPANT_NOT_FOUND':
        return 'requestInformation.snackbar.clientParticipantMissing';
      case 'COLLABORATION_VANITY_URL_MISSING':
        return 'requestInformation.snackbar.vanityUrlMissing';
      default:
        return 'requestInformation.snackbar.requestError';
    }
  }

  private createRequestInformationActionDtos(): RequestInformationActionDto[] {
    return [
      ...this.selectedDocumentsWithoutFileId.map(doc => new RequestInformationActionDto({
        documentId: doc.identification,
        caseId: this.caseRequestInformationHal.caseId,
        actionType: ActionTypeEnum.REQUEST_INFORMATION,
        informationType: InformationTypeEnum.DATA
      })),
      ...this.selectedDocumentsWithFileId.map(doc => new RequestInformationActionDto({
        documentId: doc.identification,
        caseId: this.caseRequestInformationHal.caseId,
        actionType: ActionTypeEnum.REQUEST_INFORMATION,
        informationType: InformationTypeEnum.REVIEW
      })),
      ...this.requiredDocumentTitles.map(docTitle => new RequestInformationActionDto({
        caseId: this.caseRequestInformationHal.caseId,
        actionType: ActionTypeEnum.REQUEST_INFORMATION,
        informationType: InformationTypeEnum.DOCUMENT,
        documentTitle: docTitle
      }))
    ];
  }

  protected deselectDocumentWithoutFileId(document: DocumentHal) {
    const index = this.selectedDocumentsWithoutFileId.indexOf(document);
    if (index >= 0) {
      this.selectedDocumentsWithoutFileId = [
        ...this.selectedDocumentsWithoutFileId.slice(0, index),
        ...this.selectedDocumentsWithoutFileId.slice(index + 1)
      ];
    }
  }

  protected deselectDocumentWithFileId(document: DocumentHal) {
    const index = this.selectedDocumentsWithFileId.indexOf(document);
    if (index >= 0) {
      this.selectedDocumentsWithFileId = [
        ...this.selectedDocumentsWithFileId.slice(0, index),
        ...this.selectedDocumentsWithFileId.slice(index + 1)
      ];
    }
  }

  protected hasDocumentsWithoutFileId(): boolean {
    return this.documentsWithoutFileId.length > 0;
  }

  protected isSelectedCustomerWithoutEmail(): boolean {
    return !!this.selectedCustomer && !this.selectedCustomer.email?.trim();
  }

  protected getFullClientName() {
    const customer = this.caseCustomer();
    const firstName = customer?.firstName ?? '';
    const lastName = customer?.lastName ?? '';
    return (firstName + ' ' + lastName).trim();
  }
}
